import React from 'react';
import { User, UserProgress, Achievement, Level } from '../types';

interface ProfilePageProps {
  user: User | null;
  progress: UserProgress | null;
  achievements: Achievement[];
  levels: Level[];
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  user,
  progress,
  achievements,
  levels
}) => {
  const completedCount = progress?.completedLevels.length || 0;
  const totalCount = levels.length;
  const completionRate = Math.round((completedCount / totalCount) * 100);

  const getAchievementProgress = () => {
    const unlockedCount = progress?.achievements.length || 0;
    return `${unlockedCount}/${achievements.length}`;
  };

  const renderStatCard = (
    icon: string,
    value: string | number,
    label: string,
    color: string
  ) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '16px',
      textAlign: 'center',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
    }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ 
        fontSize: '24px', 
        fontWeight: '700', 
        color: color,
        marginBottom: '4px'
      }}>
        {value}
      </div>
      <div style={{ fontSize: '12px', color: '#6b7280' }}>{label}</div>
    </div>
  );

  return (
    <div className="fade-in">
      <div className="header">
        <h1>👤 个人中心</h1>
        <div className="subtitle">查看学习成果</div>
      </div>

      <div className="main-content">
        {/* 用户信息卡片 */}
        <div className="chapter-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ 
              fontSize: '64px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px',
              padding: '16px'
            }}>
              {user?.avatar || '👨‍🎓'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#1f2937',
                marginBottom: '4px'
              }}>
                {user?.nickname || '化学学习者'}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#6b7280',
                marginBottom: '4px'
              }}>
                {user?.school}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#6b7280'
              }}>
                {user?.grade} {user?.class} · 加入于{user?.joinDate}
              </div>
            </div>
          </div>
        </div>

        {/* 统计数据 */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '12px',
          marginBottom: '20px'
        }}>
          {renderStatCard('🏆', progress?.totalScore || 0, '总积分', '#3b82f6')}
          {renderStatCard('🎯', completedCount, '完成关卡', '#10b981')}
          {renderStatCard('📅', progress?.streak || 0, '连续天数', '#f59e0b')}
          {renderStatCard('⭐', completionRate + '%', '完成率', '#8b5cf6')}
        </div>

        {/* 周榜和月榜 */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '12px',
          marginBottom: '20px'
        }}>
          {renderStatCard('📊', progress?.weeklyScore || 0, '本周积分', '#667eea')}
          {renderStatCard('📈', progress?.monthlyScore || 0, '本月积分', '#764ba2')}
        </div>

        {/* 关卡详情 */}
        <div className="chapter-card">
          <div className="chapter-header">
            <div className="chapter-title">🎯 关卡详情</div>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '14px'
            }}>
              <span style={{ color: '#6b7280' }}>总体进度</span>
              <span style={{ fontWeight: '600', color: '#3b82f6' }}>
                {completedCount}/{totalCount} 关
              </span>
            </div>
            <div style={{ 
              background: '#e5e7eb', 
              borderRadius: '8px', 
              height: '12px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${completionRate}%`, 
                height: '100%',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          {levels.map(level => {
            const isCompleted = progress?.completedLevels.includes(level.id);
            const isUnlocked = level.unlocked;
            
            return (
              <div 
                key={level.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  background: isCompleted ? '#d1fae5' : 
                            isUnlocked ? '#ffffff' : '#f3f4f6',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  border: isCompleted ? '2px solid #10b981' : '2px solid transparent'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: isUnlocked ? '#3b82f6' : '#d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                    {isUnlocked ? level.id : '🔒'}
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: isUnlocked ? '#1f2937' : '#9ca3af'
                    }}>
                      {level.title}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      最高分：{level.bestScore || 0} · 
                      {level.stars === 0 ? '未完成' : '⭐'.repeat(level.stars)}
                    </div>
                  </div>
                </div>
                
                {isCompleted && (
                  <div style={{ fontSize: '20px' }}>✅</div>
                )}
              </div>
            );
          })}
        </div>

        {/* 成就列表 */}
        <div className="chapter-card">
          <div className="chapter-header">
            <div className="chapter-title">🏆 成就系统</div>
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280'
            }}>
              {getAchievementProgress()}
            </div>
          </div>

          {achievements.map(achievement => {
            const isUnlocked = progress?.achievements.includes(achievement.id);
            
            return (
              <div 
                key={achievement.id}
                className="achievement-item"
                style={{ 
                  opacity: isUnlocked ? 1 : 0.5,
                  background: isUnlocked ? '#f0fdf4' : '#f9fafb'
                }}
              >
                <div className="achievement-icon" style={{
                  fontSize: '40px',
                  filter: isUnlocked ? 'none' : 'grayscale(100%)'
                }}>
                  {achievement.icon}
                </div>
                <div className="achievement-info">
                  <div className="achievement-title">{achievement.title}</div>
                  <div className="achievement-desc">{achievement.description}</div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    条件：{achievement.requirement}
                  </div>
                </div>
                {isUnlocked ? (
                  <div className="achievement-unlocked">已获得</div>
                ) : (
                  <div style={{ 
                    padding: '4px 8px',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    未解锁
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 设置选项 */}
        <div className="chapter-card">
          <div className="chapter-title" style={{ marginBottom: '16px' }}>
            ⚙️ 设置
          </div>
          
          {[
            { icon: '🔔', label: '消息通知', value: '开启' },
            { icon: '📚', label: '每日提醒', value: '开启' },
            { icon: '🔊', label: '声音效果', value: '开启' },
            { icon: '🌙', label: '深色模式', value: '关闭' }
          ].map((item, index) => (
            <div 
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: index < 3 ? '1px solid #f3f4f6' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>{item.icon}</span>
                <span style={{ fontSize: '14px', color: '#374151' }}>
                  {item.label}
                </span>
              </div>
              <span style={{ 
                fontSize: '14px', 
                color: '#6b7280'
              }}>
                {item.value} →
              </span>
            </div>
          ))}
        </div>

        {/* 版本信息 */}
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          color: '#9ca3af',
          fontSize: '12px'
        }}>
          <div>高二化学闯关测试 v1.0.0</div>
          <div style={{ marginTop: '4px' }}>
            北京海淀区 · 选择性必修3 有机化学
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
